import Flicking from '@egjs/react-flicking'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useMeasure } from 'react-use'
import ConfiguratorCanvasImage from '../../images/configurator-canvas.png'
import { useToolOverlay } from '../Context/ToolOverlayContext'
import LiftConfiguratorButton from '../atoms/LiftConfiguratorButton'

const M13LiftConfigurator = ({
  heading,
  mainVideoBackground,
  mainImageBackground,
  mainImageMidground,
  mainImageForeground,
  optionGroups,
  configuratorCard,
  anchorLinkId,
}) => {
  const { setToolOverlayOpen } = useToolOverlay()
  const videoRef = useRef()
  const [videoWrapperRef, { width: videoWidth }] = useMeasure()
  const [canvasRef, { width: canvasWidth }] = useMeasure()
  const { inView, ref: containerRef } = useInView({
    threshold: 0.5,
    delay: 1000,
  })
  const [videoEnded, setVideoEnded] = useState(false)

  const defaultSelected = optionGroups.reduce(
    (acc, group) => ({
      ...acc,
      [group.optionGroupLabel]: group.options[0].title,
    }),
    {},
  )
  const [selected, setSelected] = useState(defaultSelected)
  const shouldHideForeground = Object.entries(selected).some(
    ([groupLabel, optionTitle]) =>
      optionGroups
        .find(group => group.optionGroupLabel === groupLabel)
        .options.find(option => option.title === optionTitle).hideForeground,
  )

  const changeSelection = (title, label) => {
    setSelected({ ...selected, [title]: label })
  }

  useEffect(() => {
    if (inView && !videoEnded) {
      videoRef.current?.play()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return (
    <section
      className="my-10 overflow-x-hidden lg:overflow-x-visible md:my-20"
      id={anchorLinkId}
    >
      <div className="container">
        <h2 className="u-h1 max-w-[466px] mb-5 md:mb-10 lg:mb-8">{heading}</h2>

        <div className="row-type-h">
          <div className="col-1-type-h" ref={containerRef}>
            <div className="sticky inline-flex top-5" ref={canvasRef}>
              <img
                src={ConfiguratorCanvasImage}
                alt=""
                className="w-full h-auto"
              />
              {/* <img
                src={mainImageBackground[0]?.url}
                alt=""
                className="absolute inset-0 -z-1"
              /> */}
              <div
                className="absolute h-full pointer-events-none aspect-video"
                ref={videoWrapperRef}
                style={{ left: -((videoWidth - canvasWidth) / 2) }}
              >
                <img
                  src={mainImageBackground[0]?.url}
                  alt=""
                  className={`w-auto h-full ${
                    videoEnded ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </div>
              <div
                className="absolute h-full pointer-events-none aspect-video"
                style={{ left: -((videoWidth - canvasWidth) / 2) }}
              >
                <video
                  width="auto"
                  height="100%"
                  muted
                  playsInline
                  onEnded={() => setVideoEnded(true)}
                  ref={videoRef}
                  className={`${videoEnded ? 'opacity-0' : 'opacity-100'}`}
                  fetchpriority="high"
                >
                  <source
                    src={
                      mainVideoBackground.find(
                        ({ extension }) => extension === 'mp4',
                      )?.url
                    }
                    type='video/mp4; codecs="hvc1"'
                  />
                  <source
                    src={
                      mainVideoBackground.find(
                        ({ extension }) => extension === 'webm',
                      )?.url
                    }
                    type="video/webm"
                  />
                </video>
              </div>

              {optionGroups.map(optionGroup => (
                <AnimatePresence key={optionGroup.id}>
                  {videoEnded &&
                    optionGroup.options
                      .filter(
                        option =>
                          selected[optionGroup.optionGroupLabel] ===
                            option.title && option.overlayImage[0],
                      )
                      .map(option => {
                        return option.overlayVideo[0]?.url ? (
                          <motion.video
                            width="100%"
                            height="100%"
                            key={option.overlayVideo[0]?.url}
                            className="absolute top-0 left-0 z-10 object-contain w-full h-full pointer-events-none"
                            muted
                            playsInline
                            autoPlay
                            loop
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.1 }}
                          >
                            <source
                              src={
                                option.overlayVideo.find(
                                  ({ extension }) => extension === 'mp4',
                                )?.url
                              }
                              type='video/mp4; codecs="hvc1"'
                            />
                            <source
                              src={
                                option.overlayVideo.find(
                                  ({ extension }) => extension === 'webm',
                                )?.url
                              }
                              type="video/webm"
                            />
                          </motion.video>
                        ) : (
                          <motion.img
                            src={option.overlayImage[0]?.url}
                            alt=""
                            className="absolute inset-0 z-10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.1 }}
                          />
                        )
                      })}
                </AnimatePresence>
              ))}

              {mainImageMidground[0] && (
                <img
                  src={mainImageMidground[0]?.url}
                  alt=""
                  className={`absolute inset-0 z-10 ${
                    videoEnded ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              )}
              <img
                src={mainImageForeground[0]?.url}
                alt=""
                className={`absolute inset-0 z-10 ${
                  videoEnded && !shouldHideForeground
                    ? 'opacity-100'
                    : 'opacity-0'
                }`}
              />
            </div>
          </div>
          <div className="flex items-center col-2-type-h">
            <div className="hidden space-y-9 lg:block">
              {optionGroups.map(optionGroup => (
                <div key={optionGroup.id}>
                  <h3 className="mb-2 u-meta-caps">
                    {optionGroup.optionGroupLabel}
                  </h3>

                  <div className="space-y-1">
                    {optionGroup.options.map(option => (
                      <LiftConfiguratorButton
                        key={`${optionGroup.id}-${option.id}`}
                        label={option.title}
                        selected={
                          selected[optionGroup.optionGroupLabel] ===
                          option.title
                        }
                        onClick={() =>
                          changeSelection(
                            optionGroup.optionGroupLabel,
                            option.title,
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="relative lg:hidden">
              <Flicking autoInit align="prev" moveType="freeScroll">
                {optionGroups.map(optionGroup => (
                  <div key={optionGroup.id} className="mr-5">
                    <h3 className="sticky left-0 mb-2 u-meta-caps">
                      {optionGroup.optionGroupLabel}
                    </h3>

                    <div className="flex items-center space-x-1">
                      {optionGroup.options.map(option => (
                        <LiftConfiguratorButton
                          key={`${optionGroup.id}-${option.id}`}
                          label={option.title}
                          selected={
                            selected[optionGroup.optionGroupLabel] ===
                            option.title
                          }
                          onClick={() =>
                            changeSelection(
                              optionGroup.optionGroupLabel,
                              option.title,
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </Flicking>
            </div>
          </div>
        </div>
        <div className="flex">
          <div className="col-3-type-h">
            <div className="pl-0 column-indent">
              <div className="flex items-center px-6 py-4 space-x-5 bg-white shadow-box rounded-[5px] mb-10 mt-11 md:mt-14 xl:mt-5">
                <div className="flex-shrink-0">
                  <img
                    src={configuratorCard[0]?.image[0]?.url}
                    alt=""
                    className="w-[70px] h-auto"
                  />
                </div>
                <div>
                  <div
                    className="mb-2 u-p-small link-wrapper"
                    dangerouslySetInnerHTML={{
                      __html: configuratorCard[0]?.body,
                    }}
                  />
                  <div className="flex items-center space-x-1.5">
                    <img
                      src={configuratorCard[0]?.contactPhoto[0]?.url}
                      alt=""
                      className="w-[18px] h-auto rounded-full"
                    />
                    <button
                      type="button"
                      className="underline transition-all u-p-small hover:text-red-as"
                      onClick={() => setToolOverlayOpen(true)}
                    >
                      {configuratorCard[0]?.contactLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default M13LiftConfigurator
